import { createColumnHelper } from '@tanstack/react-table';
import { groupBy } from 'lodash';
import { useMemo } from 'react';

import { AssetClass, instanceOfAssetClass } from '../../../_openapi/portfolio-optimizer';
import { AssetClassTag } from '../../../widgets/AssetClassTag';
import {
    baseCol,
    excludeTradesCol,
    expanderCol,
    moneyCol,
    numberCol,
} from '../../../widgets/Table/columnHelpers/colDef';
import { CommonTradePlanEntry, getTradeAggregates, TradeTableProps } from './tradeExecutionUtils';
import { TradesTable } from './TradesTable';

interface TradePlanEntry extends CommonTradePlanEntry {
    assetClass: AssetClass | undefined;
}

const columnHelper = createColumnHelper<TradePlanEntry>();

export const TradesByAssetClass = ({
    secondaryGrouping,
    filteredTrades,
    excludeTrades,
    viewAsset,
    viewPortfolio,
    viewTrade,
    viewAdvanced,
}: TradeTableProps) => {
    const data: TradePlanEntry[] = useMemo(() => {
        if (!filteredTrades) {
            return [];
        }

        const grouped = groupBy(filteredTrades, (trade) => trade.asset?.assetClass);
        return Object.entries(grouped).map(([key, trades]) => {
            return {
                assetClass: instanceOfAssetClass(key) ? (key as AssetClass) : undefined,
                trades,
                ...getTradeAggregates(trades),
            };
        });
    }, [filteredTrades]);

    const columns = [
        columnHelper.display(expanderCol()),
        columnHelper.accessor('assetClass', {
            ...baseCol({ id: 'assetClass' }),
            cell: ({ getValue }) => <AssetClassTag assetClass={getValue()} />,
        }),
        columnHelper.accessor('tradesCount', numberCol({ id: 'tradesCount', format: { fractionDigits: 0 } })),
        columnHelper.accessor('totalQuantity', numberCol({ id: 'totalQuantity', format: { fractionDigits: 0 } })),
        columnHelper.accessor('totalValue', moneyCol({ id: 'totalValue' })),
        columnHelper.display(excludeTradesCol(excludeTrades)),
    ];

    return (
        <TradesTable
            data={data}
            secondaryGrouping={secondaryGrouping}
            columns={columns}
            excludeTrades={excludeTrades}
            viewAsset={viewAsset}
            viewPortfolio={viewPortfolio}
            viewTrade={viewTrade}
            viewAdvanced={viewAdvanced}
        />
    );
};
